import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { roundsApi } from '../api/roundsApi';
import {
  DEFAULT_STUDENT_CODING_LANGUAGE,
  isFreeChoiceCodingLanguage,
} from '../constants/codingLanguages';
import { useAppSelector } from '../store';
import { launchRoundSession } from '../utils/launchRoundSession';
import {
  buildQuickLaunchHint,
  practiceQuickLaunchTarget,
  QUICK_LAUNCH_PRACTICE_HINT,
  resolveQuickLaunchTarget,
  type QuickLaunchTarget,
} from '../utils/quickLaunchUtils';

export interface UseQuickLaunchResult {
  target: QuickLaunchTarget | null;
  hint: string;
  isLoading: boolean;
  isLaunching: boolean;
  error: string | null;
  pendingConfirm: QuickLaunchTarget | null;
  selectedLanguage: string;
  showLanguagePicker: boolean;
  launch: () => Promise<void>;
  confirmLaunch: (language?: string) => Promise<void>;
  cancelConfirm: () => void;
  setSelectedLanguage: (language: string) => void;
  refresh: () => Promise<void>;
}

const needsLanguagePicker = (launchTarget: QuickLaunchTarget): boolean =>
  launchTarget.roundType === 'coding' &&
  isFreeChoiceCodingLanguage(launchTarget.roundConfig?.language);

export const useQuickLaunch = (enabled = true): UseQuickLaunchResult => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [target, setTarget] = useState<QuickLaunchTarget | null>(null);
  const [hint, setHint] = useState(QUICK_LAUNCH_PRACTICE_HINT);
  const [isLoading, setIsLoading] = useState(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<QuickLaunchTarget | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(DEFAULT_STUDENT_CODING_LANGUAGE);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !enabled) {
      setTarget(null);
      setHint(QUICK_LAUNCH_PRACTICE_HINT);
      setError(null);
      setPendingConfirm(null);
      return;
    }

    setIsLoading(true);
    try {
      const { data } = await roundsApi.fetchUpcoming();
      const nextTarget = resolveQuickLaunchTarget(data.interviews || []);
      setTarget(nextTarget);
      setHint(buildQuickLaunchHint(nextTarget));
      setError(null);
    } catch {
      const fallback = practiceQuickLaunchTarget();
      setTarget(fallback);
      setHint(buildQuickLaunchHint(fallback));
    } finally {
      setIsLoading(false);
    }
  }, [enabled, isAuthenticated]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const runLaunch = useCallback(
    async (launchTarget: QuickLaunchTarget, language?: string) => {
      if (!launchTarget.interviewId || !launchTarget.roundNum || !launchTarget.roundType) {
        return;
      }

      if (launchTarget.needsAccept) {
        await roundsApi.accept(launchTarget.interviewId);
      }

      const result = await launchRoundSession(
        {
          interviewId: launchTarget.interviewId,
          interviewName: launchTarget.interviewName,
          roundNum: launchTarget.roundNum,
          roundType: launchTarget.roundType,
          ...(language ? { language } : {}),
        },
        navigate
      );

      if (result.error) {
        setError(result.error);
        setHint(result.error);
        return;
      }

      setPendingConfirm(null);
      void refresh();
    },
    [navigate, refresh]
  );

  const launch = useCallback(async () => {
    if (!target || isLaunching) {
      return;
    }

    setError(null);

    if (target.action === 'practice') {
      setIsLaunching(true);
      try {
        navigate('/interview');
      } finally {
        setIsLaunching(false);
      }
      return;
    }

    if (!target.interviewId || !target.roundNum || !target.roundType) {
      return;
    }

    if (needsLanguagePicker(target)) {
      setSelectedLanguage(DEFAULT_STUDENT_CODING_LANGUAGE);
      setPendingConfirm(target);
      return;
    }

    setIsLaunching(true);
    try {
      await runLaunch(target);
    } finally {
      setIsLaunching(false);
    }
  }, [isLaunching, navigate, runLaunch, target]);

  const confirmLaunch = useCallback(
    async (language?: string) => {
      if (!pendingConfirm || isLaunching) {
        return;
      }

      setIsLaunching(true);
      setError(null);
      try {
        await runLaunch(pendingConfirm, language);
      } finally {
        setIsLaunching(false);
      }
    },
    [isLaunching, pendingConfirm, runLaunch]
  );

  const cancelConfirm = useCallback(() => {
    if (isLaunching) {
      return;
    }
    setPendingConfirm(null);
    setError(null);
  }, [isLaunching]);

  return {
    target,
    hint,
    isLoading,
    isLaunching,
    error,
    pendingConfirm,
    selectedLanguage,
    showLanguagePicker: pendingConfirm ? needsLanguagePicker(pendingConfirm) : false,
    launch,
    confirmLaunch,
    cancelConfirm,
    setSelectedLanguage,
    refresh,
  };
};
